'use client'

import { memo, useState, useCallback } from 'react'
import { Tool, ToolHeader, ToolContent, ToolInput, ToolOutput } from './tool'
import { Markdown } from './markdown'
import { Reasoning, ReasoningTrigger, ReasoningContent } from './reasoning'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Check, X, Clock, Copy, Loader2, ChevronRight } from 'lucide-react'

// Grounding source from web search
export interface GroundingSource {
  uri: string
  title: string
}

export interface HistoryEntry {
  type: 'user' | 'assistant' | 'thinking' | 'tool_call' | 'tool_result' | 'error' | 'done' | 'chunk' | 'confirmation_request' | 'confirmation_response' | 'grounding' | 'sub_agent_call' | 'sub_agent_result' | 'meta_update'
  content?: string
  tool?: string
  args?: Record<string, unknown>
  result?: unknown
  error?: string
  id?: string
  confirmationId?: string
  approved?: boolean
  // For grounding (web search sources)
  sources?: GroundingSource[]
  searchQueries?: string[]
  // For sub-agent calls
  agent?: string
  query?: string
  run_id?: string
  usage?: { inputTokens: number; outputTokens: number; cost: number }
  ts: number
}

interface ChatMessageProps {
  entry: HistoryEntry
  // For merged tool calls - the result entry
  resultEntry?: HistoryEntry
  // For merged sub-agent calls - the result entry
  subAgentResultEntry?: HistoryEntry
  // Previous entry timestamp for duration calculation
  previousTs?: number
  // Is this the last message in the stream?
  isLast?: boolean
  // Callback for confirmation requests
  onConfirmation?: (confirmationId: string, approved: boolean) => void
  // Whether there's a pending confirmation for this entry
  isPendingConfirmation?: boolean
  // Callback for clicking sub-agent run link
  onSubAgentClick?: (runId: string) => void
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

// Sub-agent block with collapse/expand support
interface SubAgentBlockProps {
  agent: string
  query?: string
  runId?: string
  hasResult: boolean
  content?: string
  usage?: { inputTokens: number; outputTokens: number; cost: number }
  duration?: number
  timestamp: string
  onRunClick?: (runId: string) => void
}

function SubAgentBlock({
  agent,
  query,
  runId,
  hasResult,
  content,
  usage,
  duration,
  timestamp,
  onRunClick,
}: SubAgentBlockProps) {
  const [isOpen, setIsOpen] = useState(!hasResult) // Open by default when pending

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden">
      <div className="w-full rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 shadow-sm overflow-hidden">
        {/* Header - clickable to toggle */}
        <div
          className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-purple-100/50 dark:hover:bg-purple-900/30 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {!hasResult ? (
              <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
            ) : (
              <div className="p-1 rounded-md bg-purple-100 dark:bg-purple-900/50">
                <svg className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300 truncate">
              agent.{agent}
            </span>
            {runId && (
              <span
                className="text-xs text-purple-500 dark:text-purple-400 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  onRunClick?.(runId)
                }}
              >
                → {runId.split('-').slice(-1)[0]}
              </span>
            )}
          </div>
          <ChevronRight
            className={`h-4 w-4 text-purple-400 transition-transform duration-200 ${
              isOpen ? 'rotate-90' : ''
            }`}
          />
        </div>

        {/* Collapsible content - styled like ToolContent but purple */}
        {isOpen && (
          <div className="px-4 pb-3 pt-0 border-t border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">
            <div className="space-y-3 pt-3">
              {/* Input */}
              {query && (
                <div className="space-y-1.5 w-full max-w-full">
                  <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Input</span>
                  <div className="rounded-md border border-purple-200 dark:border-purple-800 bg-background px-3 py-2 w-full max-w-full overflow-hidden">
                    <pre className="text-xs overflow-auto max-h-[200px] whitespace-pre-wrap break-words font-mono">
                      {query}
                    </pre>
                  </div>
                </div>
              )}
              {/* Output */}
              {content && (
                <div className="space-y-1.5 w-full max-w-full">
                  <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Output</span>
                  <div className="rounded-md border border-purple-200 dark:border-purple-800 bg-background px-3 py-2 w-full max-w-full overflow-hidden">
                    <pre className="text-xs overflow-auto max-h-[300px] whitespace-pre-wrap break-words font-mono">
                      {content}
                    </pre>
                  </div>
                </div>
              )}
              {/* Usage */}
              {usage && (
                <div className="text-[10px] text-purple-500 dark:text-purple-400 font-mono">
                  {usage.inputTokens}→{usage.outputTokens} tokens · ${usage.cost.toFixed(4)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 ml-1">
        <span>{timestamp}</span>
        {duration !== undefined && duration > 0 && (
          <>
            <span className="opacity-40">·</span>
            <Clock className="h-3 w-3 opacity-60" />
            <span>{formatDuration(duration)}</span>
          </>
        )}
      </div>
    </div>
  )
}

export const ChatMessage = memo(function ChatMessage({ entry, resultEntry, subAgentResultEntry, previousTs, isLast, onConfirmation, isPendingConfirmation, onSubAgentClick }: ChatMessageProps) {
  const timestamp = new Date(entry.ts).toLocaleTimeString()
  const [copied, setCopied] = useState(false)

  // Calculate duration from previous entry if available
  const duration = previousTs ? entry.ts - previousTs : undefined

  const handleCopy = useCallback(() => {
    if (entry.content) {
      navigator.clipboard.writeText(entry.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [entry.content])

  switch (entry.type) {
    case 'user':
      return (
        <div className="flex justify-end">
          <div className="max-w-[80%] bg-primary text-primary-foreground rounded-2xl px-4 py-3">
            <p className="text-sm whitespace-pre-wrap break-words">{entry.content}</p>
            <span className="text-xs opacity-70 mt-1 block">{timestamp}</span>
          </div>
        </div>
      )

    case 'assistant': {
      return (
        <div className="w-full max-w-full min-w-0 overflow-hidden group">
          <Markdown content={entry.content || ''} />
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span>{timestamp}</span>
              {duration !== undefined && duration > 0 && (
                <>
                  <span className="opacity-40">·</span>
                  <Clock className="h-3 w-3 opacity-60" />
                  <span>{formatDuration(duration)}</span>
                </>
              )}
              {entry.usage && (
                <>
                  <span className="opacity-40">·</span>
                  <span
                    className="font-mono text-[10px] text-muted-foreground/70"
                    title={`Input: ${entry.usage.inputTokens.toLocaleString()} | Output: ${entry.usage.outputTokens.toLocaleString()} | Cost: $${entry.usage.cost.toFixed(4)}`}
                  >
                    {entry.usage.inputTokens.toLocaleString()}→{entry.usage.outputTokens.toLocaleString()}
                  </span>
                </>
              )}
            </div>
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
              title="Copy raw response"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
      )
    }

    case 'thinking': {
      const thinkingDuration = previousTs ? entry.ts - previousTs : undefined
      return (
        <div className="w-full max-w-full min-w-0 overflow-hidden">
          <Reasoning isStreaming={isLast}>
            <ReasoningTrigger />
            <ReasoningContent>{entry.content || ''}</ReasoningContent>
          </Reasoning>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 ml-1">
            <span>{timestamp}</span>
            {thinkingDuration !== undefined && thinkingDuration > 0 && (
              <>
                <span className="opacity-40">·</span>
                <Clock className="h-3 w-3 opacity-60" />
                <span>{formatDuration(thinkingDuration)}</span>
              </>
            )}
          </div>
        </div>
      )
    }
    
    case 'tool_call': {
      // Determine status based on result entry
      let status: 'pending' | 'success' | 'error' = 'pending'
      let result: unknown = undefined
      let error: string | undefined = undefined
      let toolDuration: number | undefined = undefined
      
      if (resultEntry) {
        status = resultEntry.error ? 'error' : 'success'
        result = resultEntry.result
        error = resultEntry.error
        toolDuration = resultEntry.ts - entry.ts
      }
      
      return (
        <div className="w-full max-w-full min-w-0 overflow-hidden">
           <Tool
             defaultOpen={status === 'pending'}
             isOpen={status === 'pending' ? true : undefined}
             className="w-full"
           >
             <ToolHeader
               tool={entry.tool || 'unknown'}
               state={status}
             />
             <ToolContent>
               <ToolInput input={entry.args || {}} />
               <ToolOutput output={result} error={error} />
             </ToolContent>
           </Tool>
           <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 ml-1">
             <span>{timestamp}</span>
             {toolDuration !== undefined && toolDuration > 0 && (
               <>
                 <span className="opacity-40">·</span>
                 <Clock className="h-3 w-3 opacity-60" />
                 <span>{formatDuration(toolDuration)}</span>
               </>
             )}
             {entry.usage && (
               <>
                 <span className="opacity-40">·</span>
                 <span
                   className="font-mono text-[10px] text-muted-foreground/70"
                   title={`Input: ${entry.usage.inputTokens.toLocaleString()} | Output: ${entry.usage.outputTokens.toLocaleString()} | Cost: $${entry.usage.cost.toFixed(4)}`}
                 >
                   {entry.usage.inputTokens.toLocaleString()}→{entry.usage.outputTokens.toLocaleString()}
                 </span>
               </>
             )}
           </div>
        </div>
      )
    }

    case 'tool_result':
      // Tool results are now merged with tool_call, skip rendering
      return null

    case 'sub_agent_call': {
      const hasResult = !!subAgentResultEntry
      const subAgentDuration = subAgentResultEntry ? subAgentResultEntry.ts - entry.ts : undefined
      const subAgentContent = subAgentResultEntry?.content
      const subAgentUsage = subAgentResultEntry?.usage
      // run_id comes from result entry (sub_agent_call is written before spawn, so it doesn't have run_id)
      const subAgentRunId = subAgentResultEntry?.run_id

      return (
        <SubAgentBlock
          agent={entry.agent || 'unknown'}
          query={entry.query}
          runId={subAgentRunId}
          hasResult={hasResult}
          content={subAgentContent}
          usage={subAgentUsage}
          duration={subAgentDuration}
          timestamp={timestamp}
          onRunClick={onSubAgentClick}
        />
      )
    }

    case 'sub_agent_result':
      // Sub-agent results are merged with sub_agent_call, skip rendering
      return null

    case 'grounding':
      return (
        <div className="flex justify-start">
          <div className="max-w-[80%] min-w-0 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-2xl px-4 py-3 overflow-hidden">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Web Search</span>
            </div>
            {entry.searchQueries && entry.searchQueries.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-muted-foreground">Queries: </span>
                <span className="text-xs">{entry.searchQueries.join(', ')}</span>
              </div>
            )}
            {entry.sources && entry.sources.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Sources:</span>
                <ul className="space-y-1">
                  {entry.sources.slice(0, 5).map((source, i) => (
                    <li key={i} className="text-xs">
                      <a 
                        href={source.uri} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline truncate block"
                      >
                        {source.title || source.uri}
                      </a>
                    </li>
                  ))}
                  {entry.sources.length > 5 && (
                    <li className="text-xs text-muted-foreground">
                      +{entry.sources.length - 5} more sources
                    </li>
                  )}
                </ul>
              </div>
            )}
            <span className="text-xs text-muted-foreground mt-2 block">{timestamp}</span>
          </div>
        </div>
      )

    case 'error':
      return (
        <div className="flex justify-start">
          <div className="max-w-[80%] min-w-0 bg-destructive/10 border border-destructive/20 rounded-2xl px-4 py-3 overflow-hidden">
            <span className="text-xs text-destructive font-medium">Error</span>
            <p className="whitespace-pre-wrap break-words text-sm mt-1 text-destructive overflow-auto max-h-60">
              {entry.content}
            </p>
            <span className="text-xs text-muted-foreground mt-1 block">{timestamp}</span>
          </div>
        </div>
      )

    case 'done':
      return (
        <div className="text-center text-sm text-muted-foreground py-2">
          — Agent finished at {timestamp} —
        </div>
      )

    case 'confirmation_request':
      return (
        <div className="flex justify-start">
          <div className="max-w-[80%] bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <span className="font-medium text-amber-800 dark:text-amber-200">Confirmation Required</span>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
              The agent wants to execute: <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">{entry.tool}</code>
            </p>
            {entry.args && Object.keys(entry.args).length > 0 && (
              <pre className="text-xs bg-amber-100 dark:bg-amber-900/50 p-2 rounded mb-3 overflow-auto max-h-32">
                {JSON.stringify(entry.args, null, 2)}
              </pre>
            )}
            {isPendingConfirmation && onConfirmation && entry.confirmationId && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onConfirmation(entry.confirmationId!, true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onConfirmation(entry.confirmationId!, false)}
                >
                  <X className="h-4 w-4 mr-1" />
                  Deny
                </Button>
              </div>
            )}
            <span className="text-xs text-muted-foreground mt-2 block">{timestamp}</span>
          </div>
        </div>
      )

    case 'confirmation_response':
      return (
        <div className="flex justify-start">
          <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
            entry.approved 
              ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {entry.approved ? (
                <>
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">Operation approved</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-sm text-red-700 dark:text-red-300">Operation denied</span>
                </>
              )}
            </div>
            <span className="text-xs text-muted-foreground mt-1 block">{timestamp}</span>
          </div>
        </div>
      )

    default:
      return null
  }
})
